<script lang="ts">
  import BooksIcon from '$lib/assets/icons/BooksIcon.svelte'
  import ComicsIcon from '$lib/assets/icons/ComicsIcon.svelte'
  import WebnovelIcon from '$lib/assets/icons/WebnovelIcon.svelte'
  import WebtoonIcon from '$lib/assets/icons/WebtoonIcon.svelte'
  import DocsIcon from '$lib/assets/icons/DocsIcon.svelte'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import CircleCheck from '@lucide/svelte/icons/circle-check'
  import type { Component } from 'svelte'

  type StatAccent = 'green' | 'blue' | 'peach' | 'sky' | 'mauve'
  type Stat = { label: string; value: string | number; accent?: StatAccent }

  let {
    eyebrow = 'welcome',
    title,
    kindStats = [],
    progressStats = [],
    /** @deprecated prefer kindStats + progressStats */
    stats = [],
  }: {
    eyebrow?: string
    title: string
    kindStats?: Stat[]
    progressStats?: Stat[]
    stats?: Stat[]
  } = $props()

  const accentClass: Record<StatAccent, string> = {
    green: 'text-latte-green dark:text-macchiato-green',
    blue: 'text-latte-blue dark:text-macchiato-blue',
    peach: 'text-latte-peach dark:text-macchiato-peach',
    sky: 'text-latte-sky dark:text-macchiato-sky',
    mauve: 'text-latte-mauve dark:text-macchiato-mauve',
  }

  const kindIcons: Record<string, Component<any>> = {
    novel: BooksIcon,
    manga: ComicsIcon,
    webnovel: WebnovelIcon,
    webtoon: WebtoonIcon,
  }

  const progressIcons: Record<string, Component<any>> = {
    'in progress': BookOpen,
    completed: CircleCheck,
  }

  const kinds = $derived(kindStats.length ? kindStats : stats)
  const progress = $derived(progressStats)
</script>

{#snippet statChip(stat: Stat, iconMode: 'kind' | 'progress' | 'none' = 'none')}
  {@const Icon =
    iconMode === 'kind'
      ? (kindIcons[stat.label] ?? DocsIcon)
      : iconMode === 'progress'
        ? progressIcons[stat.label]
        : null}
  {@const isLucide = iconMode === 'progress'}
  <div
    class="flex shrink-0 flex-col justify-center gap-0.5 rounded-md border border-latte-surface1/60 bg-latte-mantle/45 px-2.5 py-1.5 shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/45"
  >
    <span class="text-[8px] leading-none font-light {accentClass[stat.accent ?? 'blue']}">
      {stat.label}
    </span>
    <div class="flex items-center gap-1.5 leading-none">
      {#if Icon && isLucide}
        <Icon
          class="size-3.5 shrink-0 {accentClass[stat.accent ?? 'blue']}"
          strokeWidth={1.75}
        />
      {:else if Icon}
        <Icon
          size="sm"
          strokeWidth={1.75}
          class="size-3.5 {accentClass[stat.accent ?? 'blue']}"
        />
      {/if}
      <span class="tabular-nums text-sm font-medium text-latte-text dark:text-macchiato-text">
        {stat.value}
      </span>
    </div>
  </div>
{/snippet}

{#snippet divider()}
  <div
    class="mx-1 hidden h-8 w-px shrink-0 self-center bg-latte-surface1/70 sm:block dark:bg-macchiato-surface1/70"
    aria-hidden="true"
  ></div>
{/snippet}

<div
  class="mx-4 mt-3 mb-4 flex flex-wrap items-end gap-2 sm:mx-10 sm:gap-3"
>
  <div
    class="flex min-w-0 flex-col gap-0.5 rounded-md border border-latte-surface1/60 bg-latte-mantle/85 px-3 py-1.5 shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/85"
  >
    <span class="text-[8px] leading-none font-light text-latte-green dark:text-macchiato-green">
      {eyebrow}
    </span>
    <h1 class="truncate text-lg font-semibold leading-tight text-latte-text dark:text-macchiato-text sm:text-xl">
      {title}
    </h1>
  </div>

  {#if kinds.length > 0}
    {@render divider()}
    <div class="flex flex-wrap items-end gap-2 sm:gap-3">
      {#each kinds as stat (stat.label)}
        {@render statChip(stat, 'kind')}
      {/each}
    </div>
  {/if}

  {#if progress.length > 0}
    {@render divider()}
    <div class="flex flex-wrap items-end gap-2 sm:gap-3">
      {#each progress as stat (stat.label)}
        {@render statChip(stat, 'progress')}
      {/each}
    </div>
  {/if}
</div>
